import { FC, memo, MouseEvent, useCallback, useMemo } from "react"
import classNames from "classnames"
import Tooltip from "../shared/Tooltip"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import VitalIcons, { VitalConfig } from "@/components/shared/VitalIcons"
import { PageRoutes } from "@/constants/routes"
import { useFastAlarmClearanceEnabled } from "@/hooks/useFastAlarmClearanceEnabled"
import { useTruncation } from "@/hooks/useTruncation"
import { useUserRole } from "@/hooks/useUserRole"
import { AlertVariant, AlertVitals, TypefaceVariant } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { ButtonVariant } from "@/types/index"
import { getAlertSystemConditionTypeIconName } from "@/utils/alertConfiguration"
import { renderWithTooltip } from "@/utils/index"
import "./index.scss"

const VITAL_CONFIGS: readonly VitalConfig[] = [
   {
      highKey: "highHeartRate",
      lowKey: "lowHeartRate",
      vitalName: "heartRate",
      vitalKey: "heartRate",
   },
   {
      highKey: "highOxygenLevel",
      lowKey: "lowOxygenLevel",
      vitalName: "oxygenLevel",
      vitalKey: "oxygenLevel",
   },
] as const

export interface AlertWearableProps {
   inmateName: string
   bookingNumber?: string | null
   alertVariant?: keyof typeof AlertVariant
   typefaceColor?: keyof typeof TypefaceVariant
   systemConditionType?: AlertSystemConditionType
   vitals?: AlertVitals
   location?: string
   isClickable?: boolean
   chargeLevel?: number | null
   chargeThreshold?: number
   onClearAlert?: () => void
   onSwapDevice?: () => void
   onClick?: () => void
   onViewDetails?: () => void
}

const AlertWearable: FC<AlertWearableProps> = ({
   inmateName,
   bookingNumber,
   location,
   alertVariant,
   typefaceColor,
   systemConditionType,
   chargeThreshold = 20,
   chargeLevel,
   vitals,
   isClickable = false,
   onClearAlert = () => {},
   onSwapDevice = () => {},
   onClick = () => {},
   onViewDetails = () => {},
}) => {
   const [locationRef, isLocationTruncated] =
      useTruncation<HTMLSpanElement>(location)
   const [inmateNameRef, isInmateNameTruncated] =
      useTruncation<HTMLDivElement>(inmateName)
   const [bookingNumberRef, isBookingNumberTruncated] =
      useTruncation<HTMLSpanElement>(bookingNumber)

   const fastAlarmClearanceEnabled = useFastAlarmClearanceEnabled()
   const userCanAccessDetailView = useUserRole(
      PageRoutes.INMATE_DETAIL_OVERVIEW
   )

   // const locationName = useMemo(() => {
   //    return outOfBounds ? "Out of Bounds" : location
   // }, [location, outOfBounds])

   const stopEvent = (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
   }

   const handleSwapDeviceClick = useCallback(
      (event: MouseEvent) => {
         stopEvent(event)
         onSwapDevice()
      },
      [onSwapDevice]
   )

   const handleClearAlertClick = useCallback(
      (event: MouseEvent) => {
         stopEvent(event)
         onClearAlert()
      },
      [onClearAlert]
   )

   const handleViewDetailsClick = useCallback(
      (event: MouseEvent) => {
         stopEvent(event)
         onViewDetails()
      },
      [onViewDetails]
   )

   const typefaceIconColor = IconColor.WHITE

   const alertVariantClassName = alertVariant
      ? `alert-wearable--${AlertVariant[alertVariant]}`
      : ""

   const typefaceClassName = typefaceColor
      ? `alert-wearable__typeface--${TypefaceVariant[typefaceColor]}`
      : ""

   const isLowBattery = useMemo(
      () => !!chargeLevel && chargeLevel <= chargeThreshold,
      [chargeThreshold, chargeLevel]
   )

   return (
      <div
         className={classNames(
            "alert-wearable",
            alertVariantClassName,
            typefaceClassName,
            { ["alert-wearable--clickable"]: isClickable }
         )}
         onClick={onClick}
      >
         <div className="alert-wearable__header">
            <div className="alert-wearable__title">
               <Icon
                  className={classNames({
                     "alert-wearable__battery-icon": isLowBattery,
                  })}
                  size="16"
                  color={IconColor.WHITE}
                  name={isLowBattery ? "batteryEmpty" : "smartwatch"}
               />
               {renderWithTooltip(
                  inmateNameRef,
                  "alert-wearable__label",
                  inmateName,
                  isInmateNameTruncated
               )}
            </div>
            <div className="alert-wearable__buttons">
               {systemConditionType ===
               AlertSystemConditionType.BROKEN_WEARABLE ? (
                  <Button
                     className="alert-wearable__button-swap"
                     iconName="moveInmate"
                     iconSize="12"
                     iconPosition={ButtonIconPosition.LEFT}
                     variant={ButtonVariant.SMALL}
                     onClick={handleSwapDeviceClick}
                  >
                     Swap device
                  </Button>
               ) : (
                  fastAlarmClearanceEnabled && (
                     <Button
                        className="alert-wearable__button"
                        iconName="clearAlerts"
                        iconSize="12"
                        iconPosition={ButtonIconPosition.LEFT}
                        variant={ButtonVariant.SMALL}
                        onClick={handleClearAlertClick}
                     >
                        Clear
                     </Button>
                  )
               )}
               {userCanAccessDetailView && (
                  <Tooltip content="Go to detail overview">
                     <Button
                        variant={ButtonVariant.SMALL}
                        iconName="visual"
                        iconSize="12"
                        iconPosition={ButtonIconPosition.LEFT}
                        onClick={handleViewDetailsClick}
                     />
                  </Tooltip>
               )}
            </div>
         </div>
         {bookingNumber &&
            renderWithTooltip(
               bookingNumberRef,
               "alert-wearable__booking-number",
               bookingNumber,
               isBookingNumberTruncated
            )}
         <div className="alert-wearable__footer">
            <div className="alert-wearable__locations">
               {renderWithTooltip(
                  locationRef,
                  "alert-wearable__location",
                  location,
                  isLocationTruncated
               )}
            </div>

            <div className="alert-wearable__vitals">
               {systemConditionType ? (
                  <span className="alert-wearable__status">
                     <Icon
                        name={getAlertSystemConditionTypeIconName(
                           systemConditionType
                        )}
                        size="24"
                        color={typefaceIconColor}
                     />
                     {systemConditionType ===
                        AlertSystemConditionType.NO_VITALS && (
                        <span>No Vitals</span>
                     )}
                  </span>
               ) : (
                  <VitalIcons
                     vitals={vitals}
                     typefaceIconColor={typefaceIconColor}
                     vitalConfigs={VITAL_CONFIGS}
                  />
               )}
            </div>
         </div>
      </div>
   )
}

export default memo(AlertWearable)
